import { useState } from 'react';
import { PiArrowCounterClockwise, PiTimer } from 'react-icons/pi';
import { SettingName } from 'src/api/generated';
import { useSettings, useUpdateSetting } from 'src/hooks/api/settings';
import { Text } from 'src/modules/shared/components/atoms/text';
import NumberInput from 'src/modules/shared/components/atoms/inputs/number-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { InlineSaveFeedback } from 'src/modules/shared/components/molecules/inline-save-feedback/inline-save-feedback';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { SettingPermission } from 'src/modules/shared/types/permissions.type';

const DEFAULT_WAIT_SECONDS = 20;
const MIN_WAIT_SECONDS = 5;
const MAX_WAIT_SECONDS = 60;
const isValidWait = (value: number) =>
  Number.isInteger(value) &&
  value >= MIN_WAIT_SECONDS &&
  value <= MAX_WAIT_SECONDS;

/** Save complete response-time edits on blur or Enter through the existing tenant setting. */
export function EmployeesSettingsSection() {
  const { t } = useI18n();
  const { hasPermission: canRead } = useHasPermissions(SettingPermission.Read);
  const { hasPermission: canManage } = useHasPermissions(
    SettingPermission.ManageTenant
  );
  const { settings, loading, error } = useSettings(canRead);
  const stored = settings.find(
    ({ name }) => name === SettingName.EMPLOYEE_EXECUTION_BUFFER_WINDOW_SECONDS
  );
  const configuredDefault = Number(stored?.defaultValue);
  const defaultValue = isValidWait(configuredDefault)
    ? configuredDefault
    : DEFAULT_WAIT_SECONDS;
  const storedValue = Number(stored?.value);
  const value = isValidWait(storedValue) ? storedValue : defaultValue;
  const invalidStoredValue =
    stored?.hasInvalidValue === true ||
    (stored !== undefined && !isValidWait(storedValue));
  const [draft, setDraft] = useState<string | null>(null);
  const save = useUpdateSetting(() => setDraft(null));
  const displayedValue = draft ?? String(value);
  const valid =
    displayedValue.trim() !== '' && isValidWait(Number(displayedValue));
  const disabled = !canManage || save.loading;
  const persist = (next: number) => {
    if (disabled || error || !isValidWait(next)) return;
    save.updateSetting({
      name: SettingName.EMPLOYEE_EXECUTION_BUFFER_WINDOW_SECONDS,
      value: String(next),
      scope: 'tenant',
    });
  };
  const commit = () => {
    if (valid && (Number(displayedValue) !== value || invalidStoredValue)) {
      persist(Number(displayedValue));
    }
  };
  if (!canRead) return <Text>{t('propertyLinks.noPermission')}</Text>;
  if (loading) return <Loading />;
  if (error) {
    return (
      <ErrorState
        title={t('settings.employees.error.title')}
        description={t('settings.employees.error.description')}
      />
    );
  }

  return (
    <InlineFormSection
      title={t('settings.employees.responseWait.title')}
      description={t('settings.employees.responseWait.description')}
      icon={<PiTimer className="h-5 w-5" aria-hidden />}
      headerAction={
        canManage &&
        (value !== defaultValue || invalidStoredValue) && (
          <Button
            size="sm"
            variant="neutral"
            appearance="ghost"
            disabled={save.loading}
            onClick={() => {
              setDraft(String(defaultValue));
              persist(defaultValue);
            }}
          >
            <PiArrowCounterClockwise className="h-3.5 w-3.5" aria-hidden />
            {t('settings.tenant.reset')}
          </Button>
        )
      }
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-24">
              <NumberInput
                id="response-wait-seconds"
                aria-label={t('settings.employees.responseWait.label')}
                aria-describedby="response-wait-help"
                aria-invalid={!valid}
                value={displayedValue}
                disabled={disabled}
                min={MIN_WAIT_SECONDS}
                max={MAX_WAIT_SECONDS}
                step={1}
                onChange={(event) => {
                  setDraft(event.target.value);
                  save.reset();
                }}
                onBlur={commit}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <Text size="small" color="secondary">
              {t('settings.employees.responseWait.unit')}
            </Text>
          </div>
          <InlineSaveFeedback
            saving={save.loading}
            saved={save.success}
            error={
              save.error ? t('settings.employees.update.error') : undefined
            }
            onRetry={canManage ? save.retry : undefined}
          />
        </div>
        <div id="response-wait-help" role={valid ? undefined : 'alert'}>
          <Text size="tiny" color={valid ? 'secondary' : 'danger'}>
            {t('settings.employees.responseWait.rangeWarning', {
              min: MIN_WAIT_SECONDS,
              max: MAX_WAIT_SECONDS,
            })}
          </Text>
        </div>
      </div>
    </InlineFormSection>
  );
}
